/* @jsx h */
import { defineComponent, h } from "vue";
import { ContextRequirement } from "@lemmonhq/types";

export type ContextRequirementEditorProps = {
  value: ContextRequirement;
  onChange: (requirement: ContextRequirement) => void;
};

export const ContextRequirementEditor =
  defineComponent<ContextRequirementEditorProps>({
    name: "ContextRequirementEditor",
    props: ["value", "onChange"],
    setup(props) {
      const updateField = (field: keyof ContextRequirement, value: any) => {
        props.onChange({
          ...props.value,
          [field]: value,
        });
      };

      const handleValueChange = (rawValue: string) => {
        let processedValue: boolean | string | number = rawValue;

        // Try to convert to appropriate type
        if (rawValue === "true") processedValue = true;
        else if (rawValue === "false") processedValue = false;
        else if (!isNaN(Number(rawValue)) && rawValue.trim() !== "") {
          processedValue = Number(rawValue);
        }

        updateField("value", processedValue);
      };

      const getValueAsString = () => {
        return String(props.value.value || "");
      };

      const commonContextKeys = [
        { value: "firstTimeCustomer", label: "First Time Customer" },
        { value: "membershipLevel", label: "Membership Level" },
        { value: "orderCount", label: "Order Count" },
        { value: "totalSpent", label: "Total Amount Spent" },
        { value: "daysSinceLastOrder", label: "Days Since Last Order" },
      ];

      return () => (
        <div class="card border">
          <div class="card-body p-4">
            <h4 class="card-title text-accent text-lg flex items-center gap-2">
              <div class="w-2 h-2 bg-secondary rounded-full"></div>
              Context Requirement
            </h4>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Context Key</span>
                </label>
                <select
                  value={props.value.key}
                  onChange={(e) =>
                    updateField("key", (e.target as HTMLSelectElement).value)
                  }
                  class="select select-bordered focus:select-primary"
                >
                  <option value="">Select a context key</option>
                  {commonContextKeys.map((key) => (
                    <option key={key.value} value={key.value}>
                      {key.label}
                    </option>
                  ))}
                </select>
                <label class="label">
                  <span class="label-text-alt">Or type custom key</span>
                </label>
                <input
                  type="text"
                  value={props.value.key}
                  onInput={(e) =>
                    updateField("key", (e.target as HTMLInputElement).value)
                  }
                  class="input input-bordered focus:input-primary mt-2"
                  placeholder="Custom context key"
                />
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Expected Value</span>
                </label>
                <input
                  type="text"
                  value={getValueAsString()}
                  onInput={(e) =>
                    handleValueChange((e.target as HTMLInputElement).value)
                  }
                  class="input input-bordered focus:input-primary"
                  placeholder="true, false, or any value"
                />
                <label class="label">
                  <span class="label-text-alt">
                    Current type: {typeof props.value.value}
                  </span>
                </label>

                <div class="mt-2">
                  <div class="text-xs text-base-content/70 space-y-1">
                    <div>
                      <strong>Examples:</strong>
                    </div>
                    <div>• "true" or "false" for boolean</div>
                    <div>• "5" for number</div>
                    <div>• "premium" for string</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    },
  });
